import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useCallback, useMemo } from 'react';
import { VALIDATION } from '../constants';
import { hasErrorCode } from '../utils';

interface Command {
  Name: string;
  Command: string;
}

interface CommandSelectProps {
  name: 'startCommands' | 'stopCommands';
  label: string;
  commands?: Command[];
}

interface FormValues {
  stackId: string | null;
  startCommands: string[];
  stopCommands: string[];
}

function CommandSelect({ name, label, commands = [] }: CommandSelectProps) {
  const {
    control,
    formState: { errors },
    trigger,
  } = useFormContext<FormValues>();

  const hasValidationError = useMemo(
    () =>
      hasErrorCode(errors.stackId, VALIDATION.COMMANDS_OR_STACK_REQUIRED.code),
    [errors.stackId],
  );

  const handleCommandChange = useCallback(
    (onChange: (value: string[]) => void) =>
      (event: SelectChangeEvent<string[]>) => {
        const value =
          typeof event.target.value === 'string'
            ? [event.target.value]
            : event.target.value;
        onChange(value);

        if (hasValidationError) {
          trigger('stackId');
        }
      },
    [hasValidationError, trigger],
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl fullWidth error={hasValidationError}>
          <InputLabel id={`select-${name}`}>{label}</InputLabel>
          <Select
            {...field}
            onChange={handleCommandChange(field.onChange)}
            multiple
            error={hasValidationError || !!errors[name]}
            labelId={`select-${name}`}
            label={label}
            renderValue={(selected) => selected.join(', ')}
          >
            {commands.map(({ Name: cmdName, Command: command }) => (
              <MenuItem key={command} value={command}>
                <Checkbox checked={field.value.includes(command)} />
                <ListItemText primary={cmdName} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    />
  );
}

export default CommandSelect;
